import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faHourglass } from '@fortawesome/pro-solid-svg-icons';
import { differenceInHours, isFuture, isPast } from 'date-fns';
import { FormattedMessage, defineMessages } from 'react-intl';

import { ReferralRewardState, Sport } from '__generated__/globalTypes';
import { Button } from 'atoms/buttons/Button';
import { FontAwesomeIcon } from 'atoms/icons';
import { Vertical } from 'atoms/layout/flex';
import { BodyM, BodyS } from 'atoms/typography';
import { Countdown } from 'components/Countdown';
import { ClaimReferralRewardsDialog } from 'components/referral/ClaimReferralRewardsDialog';
import { ANY_SPORT_PLAY } from 'constants/__generated__/routes';
import { useSportContext } from 'contexts/sport';
import { TimeLeft } from 'contexts/ticker/TimeLeft';
import { fantasy, glossary } from 'lib/glossary';
import { generateSportPath } from 'lib/routing/generateSportPath';

import type { ReadyToClaimReferralBar_ReferralReward } from './__generated__/index.graphql';

const messages = defineMessages({
  playTwoMoreGW: {
    id: 'Referral.ReadyToClaimReferralBar.twoMissingGW2',
    defaultMessage:
      'Play 2 Game Weeks with the cards you bought to unlock your reward. {gift}',
  },
  playTwoMore: {
    id: 'Referral.ReadyToClaimReferralBar.twoMissing',
    defaultMessage:
      'Play twice with the cards you bought to unlock your reward. {gift}',
  },
  playOneMoreGW: {
    id: 'Referral.ReadyToClaimReferralBar.oneMissingGW2',
    defaultMessage:
      'Play 1 more Game Week with the cards you bought to unlock your reward. {gift}',
  },
  playOneMore: {
    id: 'Referral.ReadyToClaimReferralBar.oneMissing',
    defaultMessage:
      'Play one more time with the cards you bought to unlock your reward. {gift}',
  },
});

const MightClaim = ({
  refereeRewards,
}: {
  refereeRewards: ReadyToClaimReferralBar_ReferralReward[];
}) => {
  const { sport } = useSportContext();
  const claimableAt = refereeRewards
    .map(r => r.claimableAt)
    .filter(Boolean)
    .sort()[0];

  const imminentClaim =
    claimableAt &&
    isFuture(claimableAt) &&
    differenceInHours(claimableAt, new Date()) < 48;

  const oneMissingGW = refereeRewards.some(
    r => r.claimableState === ReferralRewardState.NOT_CLAIMABLE_YET_1_GW_MISSING
  );

  const twoMissingGW = refereeRewards.some(
    r => r.claimableState === ReferralRewardState.NOT_CLAIMABLE_YET_2_GW_MISSING
  );

  const compose = (
    <>
      <Button
        size="medium"
        color="primary"
        to={generateSportPath(ANY_SPORT_PLAY, { sport })}
        name="compose"
      >
        <FormattedMessage {...fantasy.composeLineup} />
      </Button>
      <BodyS color="var(--c-nd-600)" className="text-center">
        <FontAwesomeIcon icon={faHourglass} />{' '}
        <FormattedMessage
          id="Referral.ReadyToClaimReferralBar.timeLeft"
          defaultMessage="or wait {timeLeft}"
          values={{
            timeLeft: <TimeLeft time={claimableAt} />,
          }}
        />
      </BodyS>
    </>
  );

  if (oneMissingGW && !imminentClaim) {
    return (
      <>
        <BodyM>
          <FormattedMessage
            {...(sport === Sport.NBA
              ? messages.playOneMore
              : messages.playOneMoreGW)}
            values={{ gift: '🎁' }}
          />
        </BodyM>
        {compose}
      </>
    );
  }
  if (twoMissingGW && !imminentClaim) {
    return (
      <>
        <BodyM>
          <FormattedMessage
            {...(sport === Sport.NBA
              ? messages.playTwoMore
              : messages.playTwoMoreGW)}
            values={{ gift: '🎁' }}
          />
        </BodyM>
        {compose}
      </>
    );
  }
  return (
    <>
      <BodyM>
        <FormattedMessage
          id="Referral.ReadyToClaimReferralBar.claimableSoon2"
          defaultMessage="Your reward will be claimable soon."
        />
      </BodyM>
      <Countdown date={claimableAt} />
      <Button size="medium" color="primary" disabled>
        <FormattedMessage {...glossary.claim} />
      </Button>
    </>
  );
};

type Props = {
  refereeRewards: Array<ReadyToClaimReferralBar_ReferralReward>;
  openClaimDialog: boolean;
  toggleOpenClaimDialog: () => void;
};

export const ReadyToClaimReferralBar = ({
  refereeRewards,
  openClaimDialog,
  toggleOpenClaimDialog,
}: Props) => {
  const atLeastOneRewardIsClaimable = refereeRewards.some(
    r => r.claimableState === ReferralRewardState.CLAIMABLE
  );

  const claimableAt = refereeRewards
    .map(r => r.claimableAt)
    .filter(Boolean)
    .sort()[0];

  return (
    <>
      <Vertical>
        {!!(
          atLeastOneRewardIsClaimable ||
          // If the reward has just become claimable, the state hasn't been updated yet
          (claimableAt && isPast(claimableAt))
        ) && (
          <>
            <BodyM>
              <FormattedMessage
                id="Referral.ReadyToClaimReferralBar.yourRefereeRewardIsWaitingForYou"
                defaultMessage="Your referee reward is waiting for you! {gift}"
                values={{ gift: '🎁' }}
              />
            </BodyM>
            <Button
              size="medium"
              color="primary"
              onClick={toggleOpenClaimDialog}
              name="claim"
            >
              <FormattedMessage {...glossary.claim} />
            </Button>
          </>
        )}
        {!!claimableAt && isFuture(claimableAt) && (
          <MightClaim refereeRewards={refereeRewards} />
        )}
      </Vertical>
      <ClaimReferralRewardsDialog
        referralRewards={refereeRewards}
        open={openClaimDialog}
        onClose={toggleOpenClaimDialog}
      />
    </>
  );
};

ReadyToClaimReferralBar.fragments = {
  referralReward: gql`
    fragment ReadyToClaimReferralBar_ReferralReward on ReferralReward {
      id
      claimableState
      claimableAt
      ...ClaimReferralRewardsDialog_referralReward
    }
    ${ClaimReferralRewardsDialog.fragments.referralReward}
  ` as TypedDocumentNode<ReadyToClaimReferralBar_ReferralReward>,
};
